import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  fetchSafetyBaseline,
  setBaselineConsent,
  MemberSafetyBaseline,
} from '../../services/memberSafetyService';

type Props = {
  open: boolean;
  poolId: number;
  memberId: number;
  memberName: string;
  onClose: () => void;
};

const emptyBaseline = (memberId: number, poolId: number): MemberSafetyBaseline => ({
  member_id: memberId,
  pool_id: poolId,
  wearable_id: null,
  consent: { member_id: memberId, opted_in: false, consent_at: null },
  metrics: {},
  pilot: true,
});

const MemberSafetyBaselineDialog: React.FC<Props> = ({
  open,
  poolId,
  memberId,
  memberName,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [baseline, setBaseline] = useState<MemberSafetyBaseline | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSafetyBaseline(poolId, memberId);
      setBaseline(data);
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail ||
        e?.message ||
        '서버에 연결실패. 백엔드 실행 여부를 확인';
      setError(`기준선을 불러오기 실패. (${detail})`);
      // 동의 스위치는 이 창에만 있으므로, 실패해도 기본 UI는 유지
      setBaseline((prev) => prev ?? emptyBaseline(memberId, poolId));
    } finally {
      setLoading(false);
    }
  }, [poolId, memberId]);

  useEffect(() => {
    if (open) {
      setBaseline(null);
      load();
    }
  }, [open, load]);

  const handleConsent = async (optedIn: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const res = await setBaselineConsent(poolId, memberId, optedIn);
      setBaseline(res.baseline);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || '저장 실패';
      setError(`동의 저장에 실패. (${detail})`);
    } finally {
      setLoading(false);
    }
  };

  const hr = baseline?.metrics?.heart_rate;
  const spo2 = baseline?.metrics?.spo2;
  const hrObj = typeof hr === 'object' && hr !== null ? hr : null;
  const spo2Obj = typeof spo2 === 'object' && spo2 !== null ? spo2 : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>개인 안전 기준선 — {memberName}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          설정 위치는 <strong>이 창뿐</strong>. 아래 「개인 기준선 사용 동의」를 켜면 됨.
          심박·SpO2 숫자는 수동 입력이 아니라, 동의 후 웨어러블 데이터로 자동 계산됨.
        </Alert>
        {loading && !baseline && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        {baseline && (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(baseline.consent?.opted_in)}
                  onChange={(_, v) => handleConsent(v)}
                  disabled={loading}
                />
              }
              label="개인 기준선 사용 동의"
            />
            {!baseline.consent?.opted_in && (
              <Alert severity="info" sx={{ mt: 1 }}>
                동의 후 웨어러블이 할당·수신되면 기준선(p10/p50/p90) 표시.
              </Alert>
            )}
            {baseline.snapshot_fallback && (
              <Alert severity="info" sx={{ mt: 1 }}>
                일부 지표는 저장된 스냅샷 사용 중 (실시간 버퍼 샘플 부족).
              </Alert>
            )}
            {baseline.consent?.opted_in && (
              <Box sx={{ mt: 2 }}>
                <Table size="small">
                  <TableBody>
                    {hrObj && (
                      <TableRow>
                        <TableCell>심박 (bpm)</TableCell>
                        <TableCell>
                          p10 {hrObj.p10 ?? '—'} / p50 {hrObj.p50 ?? '—'} / p90{' '}
                          {hrObj.p90 ?? '—'} (n={hrObj.sample_count})
                        </TableCell>
                      </TableRow>
                    )}
                    {spo2Obj && (
                      <TableRow>
                        <TableCell>SpO2 (%)</TableCell>
                        <TableCell>
                          p10 {spo2Obj.p10 ?? '—'} / p50 {spo2Obj.p50 ?? '—'} / p90{' '}
                          {spo2Obj.p90 ?? '—'} (n={spo2Obj.sample_count})
                        </TableCell>
                      </TableRow>
                    )}
                    {!hrObj && !spo2Obj && (
                      <TableRow>
                        <TableCell colSpan={2}>
                          샘플 부족 — 웨어러블 할당·수신 후 다시 확인
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={load} disabled={loading}>
          새로고침
        </Button>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MemberSafetyBaselineDialog;
